import { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';
import DateRangeDropdown from '../lpiReports/DateRangeDropdown';
import './FuelDatasetTab.scss';

interface SessionTokens {
  jwtToken: string;
  sessionId: string;
}

interface FuelDatasetTabProps {
  getSessionTokensFn: () => Promise<SessionTokens>;
}

interface FuelRecord {
  fuelRecordId: string;
  vehicleId: string;
  periodStartDate: string;
  periodEndDate: string;
  fuelType: string;
  fuelConsumedLiters: number;
  createdFromFile: string | null;
  lastUpdatedFromFile: string | null;
  uploadedAt: string;
}

const PAGE_SIZE = 50;

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return iso.slice(0, 10);
};

const FuelDatasetTab: FC<FuelDatasetTabProps> = ({ getSessionTokensFn }) => {
  const { t } = useTranslation();

  const [records, setRecords] = useState<FuelRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [fuelTypeOptions, setFuelTypeOptions] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  // Committed dates only update on Apply, so the date picker doesn't fetch mid-selection.
  const [committedStartDate, setCommittedStartDate] = useState<Date | null>(null);
  const [committedEndDate, setCommittedEndDate] = useState<Date | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Load fuel-type filter options once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { jwtToken, sessionId } = await getSessionTokensFn();
        const res = await axios.get<{ fuelTypes: string[] }>(
          `${process.env.REACT_APP_LPI_API_URL}/cpi/fuel-types`,
          { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
        );
        if (!cancelled) setFuelTypeOptions(res.data.fuelTypes ?? []);
      } catch {
        if (!cancelled) setFuelTypeOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getSessionTokensFn]);

  const fetchRecords = useCallback(async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const { jwtToken, sessionId } = await getSessionTokensFn();
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      });
      if (debouncedSearch) params.set('vehicleId', debouncedSearch);
      if (fuelType) params.set('fuelType', fuelType);
      if (committedStartDate) params.set('periodStart', format(committedStartDate, 'yyyy-MM-dd'));
      if (committedEndDate) params.set('periodEnd', format(committedEndDate, 'yyyy-MM-dd'));

      const res = await axios.get<{ records: FuelRecord[]; total: number }>(
        `${process.env.REACT_APP_LPI_API_URL}/cpi/fuel?${params}`,
        { headers: { Authorization: `Session ${jwtToken}`, 'X-Session-Id': sessionId } }
      );
      setRecords(res.data.records);
      setTotal(res.data.total);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        'Failed to load fuel records.';
      setFetchError(msg);
    } finally {
      setIsLoading(false);
    }
  }, [page, debouncedSearch, fuelType, committedStartDate, committedEndDate, getSessionTokensFn]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="fuel-dataset">
      <div className="fuel-dataset__header">
        <div>
          <h1>{t('Fuel data')}</h1>
          <p className="fuel-dataset__subtitle">
            {t('Fuel consumption records ingested from fuel files.')}
          </p>
        </div>
      </div>

      <div className="fuel-dataset__controls">
        <DateRangeDropdown
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          isLoading={isLoading}
        />
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setCommittedStartDate(startDate);
            setCommittedEndDate(endDate);
            setPage(1);
          }}
          disabled={!startDate || !endDate}
        >
          {t('Apply')}
        </button>
      </div>

      <div className="fuel-dataset__card">
        <div className="fuel-dataset__toolbar">
          <div className="fuel-dataset__filters">
            <input
              type="text"
              className="fuel-dataset__search-input"
              placeholder={t('Search by vehicle ID…')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <select
              className="fuel-dataset__select"
              value={fuelType}
              onChange={(e) => {
                setFuelType(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('All fuel types')}</option>
              {fuelTypeOptions.map((ft) => (
                <option key={ft} value={ft}>
                  {ft}
                </option>
              ))}
            </select>
          </div>
          {!isLoading && (
            <span className="fuel-dataset__count">
              {total} {total === 1 ? t('record') : t('records')}
            </span>
          )}
        </div>

        {fetchError && <div className="fuel-dataset__error">{fetchError}</div>}

        <div className="fuel-dataset__table-wrap">
          <table className="fuel-dataset__table">
            <thead>
              <tr>
                <th>{t('Vehicle ID')}</th>
                <th>{t('Start date')}</th>
                <th>{t('End date')}</th>
                <th>{t('Fuel type')}</th>
                <th>{t('Quantity (L)')}</th>
                <th>{t('Source file')}</th>
                <th>{t('Uploaded at')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={7} className="fuel-dataset__cell--center">
                    {t('Loading…')}
                  </td>
                </tr>
              )}
              {!isLoading && records.length === 0 && (
                <tr>
                  <td colSpan={7} className="fuel-dataset__cell--center">
                    {t('No fuel records found for the selected filters.')}
                  </td>
                </tr>
              )}
              {!isLoading &&
                records.map((r) => {
                  const sourceFile = r.lastUpdatedFromFile ?? r.createdFromFile;
                  return (
                    <tr key={r.fuelRecordId}>
                      <td className="fuel-dataset__cell--id">{r.vehicleId}</td>
                      <td>{formatDate(r.periodStartDate)}</td>
                      <td>{formatDate(r.periodEndDate)}</td>
                      <td>{r.fuelType}</td>
                      <td>{r.fuelConsumedLiters != null ? r.fuelConsumedLiters.toLocaleString() : '—'}</td>
                      <td>
                        <span className="fuel-dataset__file" title={sourceFile ?? ''}>
                          {sourceFile ?? '—'}
                        </span>
                      </td>
                      <td>{formatDate(r.uploadedAt)}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="fuel-dataset__pagination">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage(1)}
              disabled={page === 1 || isLoading}
              aria-label={t("First page")}
            >
              «
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1 || isLoading}
              aria-label={t("Previous page")}
            >
              ‹
            </button>
            <span className="fuel-dataset__page-info">
              {t('Page')} {page} {t('of')} {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage((p) => p + 1)}
              disabled={page === totalPages || isLoading}
              aria-label={t("Next page")}
            >
              ›
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages || isLoading}
              aria-label={t("Last page")}
            >
              »
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuelDatasetTab;
